import axios from "axios";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { useAxiosHook } from "./useAxiosHook";

// MOST mutations on /liquidity are POST with the action suffix in the path
// (`/update`, `.../associations/{type}/{id}/remove`), but the router is NOT
// uniformly POST — verify the verb against app/routers/liquidity.py before
// adding a call. Two exceptions live there today:
//   DELETE /sources/{dealId}/{sourceId}   — delete a source (there is no
//                                           POST `.../remove` twin)
//   PUT    /required/{dealId}             — update required liquidity
// After every source mutation the server runs a verified-liquidity rollup
// (borrowers[].verifiedLiquidity + loan.totalVerifiedLiquidity) — those land
// via the loan_updates topic; mutation responses themselves only echo source
// data.
const ROUTE_PREFIX = "liquidity";

// One of: borrowerIds | entityIds | loanDocumentIds | subjectPropertyIds.
// Used by the granular remove-association endpoint.
export const LIQUIDITY_ASSOCIATION_TYPES = {
  BORROWER_IDS: "borrowerIds",
  ENTITY_IDS: "entityIds",
  LOAN_DOCUMENT_IDS: "loanDocumentIds",
  SUBJECT_PROPERTY_IDS: "subjectPropertyIds",
};

export const useLiquidityHook = () => {
  const {
    axiosGetIsLoading,
    axiosPostIsLoading,
    axiosDeleteIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
    axiosDeleteRequest,
  } = useAxiosHook();

  const loading =
    axiosGetIsLoading || axiosPostIsLoading || axiosDeleteIsLoading;
  const error = axiosError;

  /* ───────────── sources (per-loan list) ───────────── */

  const getLiquiditySources = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const source = axios.CancelToken.source();
    await axiosGetRequest({
      name: "Get Liquidity Sources",
      path: `${ROUTE_PREFIX}/sources/${dealId}`,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLiquiditySource = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Liquidity source added",
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, data });
    await axiosPostRequest({
      name: "Create Liquidity Source",
      path: `${ROUTE_PREFIX}/sources/${dealId}`,
      payload: data,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateLiquiditySource = async ({
    dealId,
    sourceId,
    data,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Liquidity source updated",
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, sourceId, data });
    await axiosPostRequest({
      name: "Update Liquidity Source",
      path: `${ROUTE_PREFIX}/sources/${dealId}/${sourceId}/update`,
      payload: data,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const deleteLiquiditySource = async ({
    dealId,
    sourceId,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Liquidity source deleted",
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, sourceId });
    // DELETE, not a POST `/remove` suffix — see the note on ROUTE_PREFIX above.
    await axiosDeleteRequest({
      name: "Remove Liquidity Source",
      path: `${ROUTE_PREFIX}/sources/${dealId}/${sourceId}`,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  // Granular: pull a single id from one of the source's reference arrays
  // (borrowerIds / entityIds / loanDocumentIds / subjectPropertyIds) without
  // rewriting the whole source. Use this when the operator clicks × on a
  // single chip. Response shape: { source, sources } — pass the .sources
  // array to loanFetchedDataLiquiditySourcesSet.
  const removeLiquiditySourceAssociation = async ({
    dealId,
    sourceId,
    associationType,
    associationId,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Association removed",
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
      sourceId,
      associationType,
      associationId,
    });
    const allowed = Object.values(LIQUIDITY_ASSOCIATION_TYPES);
    if (!allowed.includes(associationType)) {
      throw new Error(
        `removeLiquiditySourceAssociation: associationType must be one of ${allowed.join(", ")}`,
      );
    }
    await axiosPostRequest({
      name: "Remove Liquidity Source Association",
      path: `${ROUTE_PREFIX}/sources/${dealId}/${sourceId}/associations/${associationType}/${associationId}/remove`,
      payload: {},
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  /* ───────────── required (per-loan singleton) ───────────── */

  const getRequiredLiquidity = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const source = axios.CancelToken.source();
    await axiosGetRequest({
      name: "Get Required Liquidity",
      path: `${ROUTE_PREFIX}/required/${dealId}`,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateRequiredLiquidity = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
    // No onSuccessMsg by default — this is debounced auto-save, snackbar
    // noise every keystroke pause is too much.
  }) => {
    validateRequiredFields({ dealId, data });
    await axiosPostRequest({
      name: "Update Required Liquidity",
      path: `${ROUTE_PREFIX}/required/${dealId}`,
      payload: data,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  /* ───────────── combined read (optional) ───────────── */

  const getLiquidityCheck = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const source = axios.CancelToken.source();
    await axiosGetRequest({
      name: "Get Liquidity Check",
      path: `${ROUTE_PREFIX}/check/${dealId}`,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  return {
    loading,
    error,
    getLiquiditySources,
    createLiquiditySource,
    updateLiquiditySource,
    deleteLiquiditySource,
    removeLiquiditySourceAssociation,
    getRequiredLiquidity,
    updateRequiredLiquidity,
    getLiquidityCheck,
  };
};
